import {
  Component,
  DestroyRef,
  EventEmitter,
  HostBinding,
  inject,
  Inject,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {RecipientLimitsSnapshot} from '@gatewee/types/recipient-setting';
import {
  FeeSnapshot,
  FeeSnapshotItem,
  normalizeFeeSnapshotResponse,
  toEffectiveFeeSnapshot,
} from '@gatewee/types/fee';
import {readProfileRaw} from '@gatewee/services';
import {TranslateService} from '@ngx-translate/core';
import {TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {FeesService} from '../../_services/fees/fees.service';
import {RecipientsSettingsService} from '../../_services/settings/recipients-settings.service';
import {WithdrawalTransferFormComponent} from '../../pages/withdrawals/form/pix/withdrawal-transfer-form.component';
import {WithdrawalEmvFormComponent} from '../../pages/withdrawals/form/pix/withdrawal-emv-form.component';
import {QrScannerPaneComponent} from '../../pages/withdrawals/form/pix/qr-scanner-pane.component';

export type PixShellFlow = 'hub' | 'transfer' | 'emv' | 'qrScanner';

@Component({
  selector: 'app-pix-area',
  templateUrl: './pix-area.component.html',
  styleUrls: ['./pix-area.component.css'],
  standalone: false,
})
export class PixAreaComponent implements OnInit {
  /**
   * When true (e.g. dashboard), this component owns a `gatewee-drawer` around the PIX
   * shell. When false (e.g. Taiga dialog on withdrawals), the shell renders as a card.
   */
  @Input() usePageDrawer = false;

  /** Passed to the PIX transfer form: skip checkKey and the confirmed recipient step. */
  @Input() skipPixRecipientVerification = false;

  /** Synchronized with the internal page drawer when `usePageDrawer` is true. */
  pageDrawerOpen = false;

  @HostBinding('class.pix-area--page-drawer-host')
  get pageDrawerHostClass(): boolean {
    return this.usePageDrawer;
  }

  @Output() readonly closed = new EventEmitter<void>();

  /**
   * Emitted whenever the owned page drawer open state changes (dismiss, backdrop,
   * Escape or programmatic close). Lets hosts (e.g. dashboard) refresh data when
   * the drawer closes. Only meaningful when `usePageDrawer` is true.
   */
  @Output() readonly pageDrawerOpenChange = new EventEmitter<boolean>();

  /** Emitted when the top-level PIX shell flow changes (hub vs transfer vs EMV). */
  @Output() readonly flowChange = new EventEmitter<PixShellFlow>();

  activeFlow: PixShellFlow = 'hub';

  recipientLimits: RecipientLimitsSnapshot | null = null;
  feeSnapshot: FeeSnapshot | null = null;

  @ViewChild('transferForm')
  transferForm?: WithdrawalTransferFormComponent;

  @ViewChild('emvFormRef')
  emvForm?: WithdrawalEmvFormComponent;

  @ViewChild('qrScannerRef')
  qrScanner?: QrScannerPaneComponent;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly recipientsSettingsService = inject(
    RecipientsSettingsService,
  );
  private readonly feesService = inject(FeesService);

  constructor(
    private router: Router,
    private readonly translate: TranslateService,
    @Optional()
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly dialogContext: TuiDialogContext<void, void> | null,
  ) {}

  ngOnInit(): void {
    this.loadRecipientLimits();
    this.loadFeeSnapshot();
  }

  onTransferShellChange(): void {
    this.cdr.detectChanges();
  }

  private readonly limitsI18n = 'features.withdrawals.modalPix.limits';

  get hubTransactionLimitDisplay(): string {
    const limit = this.recipientLimits?.cashoutTransactionLimit;
    if (limit === null || limit === undefined) {
      return this.translate.instant(`${this.limitsI18n}.unlimited`);
    }
    return this.formatLimitCurrency(limit);
  }

  get hubDailyRemainingDisplay(): string {
    const remaining = this.recipientLimits?.dailyCashoutRemaining;
    if (remaining === null || remaining === undefined) {
      return this.translate.instant(`${this.limitsI18n}.unlimited`);
    }
    return this.formatLimitCurrency(remaining);
  }

  get hubShowDailyProgress(): boolean {
    const cap = this.recipientLimits?.dailyCashoutLimit;
    return cap !== null && cap !== undefined && cap > 0;
  }

  get hubDailyUsagePercent(): number {
    if (!this.hubShowDailyProgress || !this.recipientLimits) {
      return 0;
    }
    const cap = this.recipientLimits.dailyCashoutLimit ?? 0;
    if (cap <= 0) {
      return 0;
    }
    return Math.min(100, (this.recipientLimits.dailyCashoutUsed / cap) * 100);
  }

  get hubDailyUsedDisplay(): string {
    return this.formatLimitCurrency(
      this.recipientLimits?.dailyCashoutUsed ?? 0,
    );
  }

  get hubDailyCapDisplay(): string {
    const cap = this.recipientLimits?.dailyCashoutLimit;
    if (cap === null || cap === undefined) {
      return this.translate.instant(`${this.limitsI18n}.unlimited`);
    }
    return this.formatLimitCurrency(cap);
  }

  private loadRecipientLimits(): void {
    this.recipientsSettingsService
      .findLimits()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null)),
      )
      .subscribe((snapshot: RecipientLimitsSnapshot | null) => {
        this.recipientLimits = snapshot;
      });
  }

  private loadFeeSnapshot(): void {
    this.feesService
      .findFeeSnapshotRecipient()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(items => normalizeFeeSnapshotResponse(items)),
        catchError(() => this.resolveFeeSnapshotLegacy()),
      )
      .subscribe(snapshot => {
        this.feeSnapshot = snapshot;
      });
  }

  private resolveFeeSnapshotLegacy(): Observable<FeeSnapshot> {
    const recipientId = this.getRecipientIdFromProfile();
    if (recipientId) {
      return this.feesService.findFeeSnapshot(recipientId).pipe(
        map(raw => normalizeFeeSnapshotResponse(raw)),
        catchError(() => this.findDefaultAsSnapshot()),
      );
    }
    return this.findDefaultAsSnapshot();
  }

  private findDefaultAsSnapshot(): Observable<FeeSnapshot> {
    return this.feesService
      .findDefaultFees()
      .pipe(map((fees: FeeSnapshotItem[]) => toEffectiveFeeSnapshot(fees)));
  }

  private getRecipientIdFromProfile(): number | null {
    try {
      const profile = JSON.parse(readProfileRaw()) as {
        recipient?: {id?: number};
      };
      const id = profile?.recipient?.id;
      return typeof id === 'number' && id > 0 ? id : null;
    } catch {
      return null;
    }
  }

  private formatLimitCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  }

  /** Chave i18n do título quando o formulário preenche o header (ex.: drawer). */
  get drawerEmbeddedTitleKey(): string {
    if (this.activeFlow === 'hub') {
      return 'features.withdrawals.modalPix.title';
    }
    if (this.activeFlow === 'qrScanner') {
      return 'features.withdrawals.modalPix.qrScannerDrawerTitle';
    }
    if (this.activeFlow === 'emv') {
      return (
        this.emvForm?.drawerTitleKey ??
        'features.withdrawals.modalPix.emvDrawerTitle'
      );
    }
    return (
      this.transferForm?.currentTitleKey ??
      'features.withdrawals.modalPix.title'
    );
  }

  /** Invoked from drawer back or dialog sub-flow header. */
  handleShellBack(): void {
    if (this.activeFlow === 'qrScanner') {
      this.onQrScannerBack();
      return;
    }
    if (this.activeFlow === 'emv' && this.emvForm?.handleBack()) {
      return;
    }
    if (this.activeFlow === 'transfer' && this.transferForm?.handleBack()) {
      return;
    }
    this.onBackFromSubFlow();
  }

  /**
   * Clears shell and transfer form when the page drawer is dismissed (close / backdrop /
   * Escape) without `finishClose`. Does not emit `closed` or complete the dialog.
   */
  resetEmbedSession(): void {
    if (!this.usePageDrawer) {
      return;
    }
    this.transferForm?.reset();
    this.emvForm?.reset();
    this.activeFlow = 'hub';
  }

  setActiveFlow(next: PixShellFlow): void {
    if (this.activeFlow === next) {
      return;
    }
    if (this.activeFlow === 'transfer' && next === 'hub') {
      this.transferForm?.reset();
    }
    if (this.activeFlow === 'emv' && next === 'hub') {
      this.emvForm?.reset();
    }
    this.activeFlow = next;
    this.flowChange.emit(next);
  }

  /** Opens the page drawer (dashboard quick action). No-op when `usePageDrawer` is false. */
  openPageDrawer(): void {
    if (!this.usePageDrawer) {
      return;
    }
    this.pageDrawerOpen = true;
  }

  onPageDrawerOpenChange(open: boolean): void {
    this.pageDrawerOpen = open;
    if (!open) {
      this.resetEmbedSession();
    }
    this.pageDrawerOpenChange.emit(open);
  }

  openTransfer(): void {
    this.setActiveFlow('transfer');
  }

  openEmv(): void {
    this.setActiveFlow('emv');
  }

  openQrScanner(): void {
    this.setActiveFlow('qrScanner');
  }

  onQrDetected(raw: string): void {
    this.emvForm?.setEmvValue(raw);
    this.setActiveFlow('emv');
  }

  onQrScannerBack(): void {
    this.qrScanner?.reset();
    this.setActiveFlow('hub');
  }

  onBackFromSubFlow(): void {
    this.setActiveFlow('hub');
  }

  onEmvBack(): void {
    this.setActiveFlow('hub');
  }

  get canContinueEmv(): boolean {
    return this.emvForm?.canProceedFooter ?? false;
  }

  onEmvContinue(): void {
    const form = this.emvForm;
    if (!form?.decodedPreview) {
      return;
    }
    this.transferForm?.beginFromEmv({
      emvCode: String(form.form.get('emv')?.value ?? ''),
      decoded: form.decodedPreview,
      amountInputMode: form.amountInputMode,
    });
    this.setActiveFlow('transfer');
  }

  onEmvHandoffBack(): void {
    this.transferForm?.clearEmvHandoff();
    this.setActiveFlow('emv');
  }

  navigateTo(path: string): void {
    void this.router.navigateByUrl(path);
    this.closeModal();
  }

  /** Transfer flow requested modal close (2FA success or proof conclude). */
  onTransferCompleted(): void {
    this.finishClose();
  }

  closeModal(): void {
    this.finishClose();
  }

  private finishClose(): void {
    if (this.activeFlow !== 'hub') {
      this.flowChange.emit('hub');
    }
    this.transferForm?.reset();
    this.emvForm?.reset();
    this.activeFlow = 'hub';
    if (this.usePageDrawer) {
      this.pageDrawerOpen = false;
      this.pageDrawerOpenChange.emit(false);
    }
    this.closed.emit();
    this.dialogContext?.completeWith();
  }
}
